/**
 * The Planned cell: the whole span as ONE always-visible control
 * ("7/1 – 7/9 📅" — click opens the re-plan calendar) with the slide arrows
 * directly underneath (◀ +Nd ▶ ✕). Amber = a staged re-plan; dashed amber = a
 * push already committed in a previous update (still editable from there).
 */
import React from "react";
import { formatDateNumeric } from "../../../../../utils/format";
import "./PlanCell.css";

interface Props {
  /** Effective (staged or committed) span, iso10. */
  start: string;
  finish: string;
  /** Milestone — a single date, not a span. */
  point: boolean;
  /** Started rows: the start is history and renders muted. */
  startLocked: boolean;
  /** A re-plan is staged (differs from the committed plan). */
  moved: boolean;
  /** A previously applied push is holding this plan (pin / constraint). */
  held: boolean;
  /** Signed workday delta of the staged move; "±" = duration-only change. */
  deltaLabel: string;
  canEdit: boolean;
  /** Why the plan can't move / the start is locked (title text). */
  reason?: string;
  canSlideBack: boolean;
  disabled: boolean;
  /** Receives the span button element — the picker popover anchors to it. */
  onOpen: (el: HTMLElement) => void;
  onSlide: (dir: 1 | -1) => void;
  onReset: () => void;
}

const PlanCell: React.FC<Props> = ({
  start,
  finish,
  point,
  startLocked,
  moved,
  held,
  deltaLabel,
  canEdit,
  reason,
  canSlideBack,
  disabled,
  onOpen,
  onSlide,
  onReset,
}) => {
  const spanCls = [
    "opc-span",
    moved ? "opc-moved" : "",
    !moved && held ? "opc-held" : "",
    canEdit ? "" : "opc-locked",
  ].filter(Boolean).join(" ");
  const title = !canEdit
    ? (reason ?? "This plan can't move")
    : held && !moved
      ? "Held by an expected/pushed date from an earlier update — click to re-plan"
      : moved
        ? "Staged re-plan — click to adjust on the calendar"
        : "Click to re-plan on the calendar";

  return (
    <span className="opc">
      <button
        type="button"
        className={spanCls}
        title={title}
        disabled={disabled || !canEdit}
        onClick={(e) => onOpen(e.currentTarget)}
      >
        {point ? (
          <span>{formatDateNumeric(finish)}</span>
        ) : (
          <>
            <span className={startLocked ? "opc-startlocked" : undefined}>
              {formatDateNumeric(start)}
            </span>
            <span className="opc-sep">–</span>
            <span>{formatDateNumeric(finish)}</span>
          </>
        )}
        {canEdit && <span className="opc-cal" aria-hidden>📅</span>}
      </button>
      {canEdit && (
        <span className="opc-slide">
          <button
            type="button"
            className="opc-ar"
            title="Slide the plan 1 workday earlier"
            disabled={disabled || !canSlideBack}
            onClick={() => onSlide(-1)}
          >◀</button>
          <span className={`opc-delta${moved ? " opc-on" : ""}`}>{deltaLabel}</span>
          <button
            type="button"
            className="opc-ar"
            title="Slide the plan 1 workday later"
            disabled={disabled}
            onClick={() => onSlide(1)}
          >▶</button>
          <button
            type="button"
            className={`opc-reset${moved ? "" : " opc-ghost"}`}
            title="Reset to the planned dates"
            disabled={disabled}
            onClick={onReset}
          >✕</button>
        </span>
      )}
    </span>
  );
};

export default PlanCell;
